import cx from 'classnames';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import TopBar from '../Commons/TopBar';
import Skeleton from '../../components/Skeleton';
import UpButton from '../../components/UpButton';
import { formatMoney } from '../../helpers/utils';
import { confirmOrder, getCustomerAddresses } from '../../services/api';
import OrderItems from './Order/OrderItems';
import OrderSummary from './Order/OrderSummary';
import OrderSupport from './Order/OrderSupport';
import BottomFooter from '../../components/BottomFooter';
import OrderCustomer from './Order/OrderCustomer';
import UrbanPiperFooter from '../Commons/UrbanPiperFooter';
import OrderStatusInfo from './Order/OrderStatusInfo';
import OrderFulfillment from './Order/OrderFulfillment';
import type { OrderOrder, Customer, Settings } from '../../types/codexOrder';
import { toast } from '../../components/UpToast';
import { ApiConstants } from '../../constants/ApiConstants';
import PaymentTypeModal from './Order/PaymentTypeModal';
import TipModal from './Order/TipModal';
import {
  CashIcon,
  ChevronLeftIcon,
  ChevronUpIcon,
  PayTimeIcon,
  PhoneIcon,
} from '../../assets/Icons';

function OrderView({
  order,
  orderId,
  customer,
  settings,
  orderLoading,
  awaitingPayment,
  awaitingCheckout,
  updateTypeModalOpen,
}: {
  order: OrderOrder | undefined;
  orderId: string;
  customer: Customer | undefined;
  awaitingPayment: boolean;
  awaitingCheckout: boolean;
  settings: Settings | undefined;
  orderLoading: boolean;
  updateTypeModalOpen: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pendingPay = awaitingCheckout || awaitingPayment;
  const isDeliveryAwaitingCheckout =
    awaitingCheckout &&
    order?.details?.order_type === ApiConstants.TYPE_DELIVERY;
  const isDeliveryWoAddress =
    isDeliveryAwaitingCheckout && !customer?.address?.id;
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [tipModalOpen, setTipModalOpen] = useState(false);
  const applicablePaymentMethods =
    order?.details?.order_type &&
    settings?.payment_methods?.[order?.details?.order_type];

  // Tip for agent + Prime orderline delivery; ordertaker and other sources excluded.
  const tippingPresets = settings?.business_tipping_config ?? [];
  const isDelivery = order?.details?.order_type === ApiConstants.TYPE_DELIVERY;
  const isOrderLineAgent =
    order?.details?.order_source === ApiConstants.SOURCE_ORDER_LINE_AGENT ||
    order?.details?.created_by === ApiConstants.SOURCE_ORDER_LINE_AGENT;
  const isPrimeOrderline =
    order?.details?.order_source === ApiConstants.SOURCE_PRIME &&
    order?.details?.order_module === ApiConstants.MODULE_ORDER_LINE;
  const isTippableSource = isOrderLineAgent || isPrimeOrderline;
  const tippingEnabled =
    awaitingCheckout &&
    isTippableSource &&
    tippingPresets.length > 0 &&
    isDelivery;
  // Disabled until a delivery address is selected (required by update_order).
  const tipDisabled = isDeliveryWoAddress;
  const tipCharge = order?.details?.charges?.find(c => c.title === 'Tip');
  const hasTip = (tipCharge?.value ?? 0) > 0;

  const { mutate: callConfirmOrder, isPending } = useMutation({
    mutationFn: (params: { reference_id: string }) => confirmOrder(params),
    onSuccess: resp => {
      queryClient.setQueryData(['getOrderDetails', orderId], resp);
      void navigate(`/${orderId}/method`);
    },
    onError: err => {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.info(
        error?.response?.data?.detail ??
          'Something went wrong. Order update action failed.',
      );
    },
  });

  const { data: customerAddresses } = useQuery({
    retry: false,
    refetchOnMount: true,
    enabled: !!(
      customer?.phone &&
      order?.details?.biz_id &&
      isDeliveryAwaitingCheckout
    ),
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!customer?.phone || !order?.details?.biz_id) {
        return Promise.reject(new Error('Could not fetch addresses.'));
      }
      const response = await getCustomerAddresses(order?.details?.biz_id, {
        customer_phone: customer?.phone,
        reference_id: orderId,
        include_delivery_details: true,
        location_id: String(order?.store?.id),
      });
      return response?.data || [];
    },
    queryKey: [
      'getCustomerAddresses',
      orderId,
      customer?.phone,
      order?.details?.biz_id,
      order?.store?.id,
      undefined,
    ],
  });

  const hydratedCustomer = useMemo(() => {
    const id = customer?.address?.id;
    if (!id || !customerAddresses?.length) return customer;
    const match = customerAddresses.find(a => String(a.id) === String(id));
    if (!match) return customer;
    return {
      ...customer,
      address: {
        ...customer?.address,
        tag: match.tag,
        line_1: match.address_1,
        line_2: match.address_2,
        sub_locality: match.sub_locality,
        landmark: match.landmark,
        city: match.city,
        pin: match.pin,
        latitude: match.lat,
        longitude: match.lng,
      },
    };
  }, [customer, customerAddresses]);

  function onPayClick() {
    if (awaitingPayment) {
      void navigate(`/${orderId}/method`);
      return;
    }
    if (awaitingCheckout) {
      callConfirmOrder({ reference_id: orderId });
      return;
    }
  }

  return (
    <div className="bg-up-black-5">
      <TopBar
        logo={settings?.logo}
        loading={orderLoading}
        bizName={settings?.biz_name}
        brand={order?.details?.brand}
      />
      {orderLoading || !order ? (
        <OrderViewSkeleton />
      ) : (
        <div
          className={cx(
            'px-2 pt-4 overflow-x-hidden',
            pendingPay ? 'pb-20' : 'pb-5',
          )}>
          <OrderStatusInfo
            order={order}
            secondaryColor={settings?.brand_secondary_color}
          />
          <OrderFulfillment
            order={order}
            settings={settings}
            customer={hydratedCustomer}
            awaitingCheckout={awaitingCheckout}
            updateTypeModalOpen={updateTypeModalOpen}
            isDeliveryWoAddress={isDeliveryWoAddress}
          />
          <OrderItems items={order?.items} />
          {tippingEnabled && !hasTip && (
            <div
              onClick={
                tipDisabled
                  ? undefined
                  : () => {
                      setTipModalOpen(true);
                    }
              }
              className={cx(
                'p-4 rounded-lg bg-white mb-4 flex items-center justify-between',
                tipDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer',
              )}>
              <div className="flex items-center gap-x-2">
                <CashIcon className="text-brand-icon w-5 h-5" />
                <p className="font-bold">Add a tip</p>
              </div>
              <ChevronLeftIcon className="w-4 h-4 rotate-180 text-up-black-50" />
            </div>
          )}
          <OrderSummary
            details={order?.details}
            onTipClick={
              tippingEnabled && !tipDisabled
                ? () => {
                    setTipModalOpen(true);
                  }
                : undefined
            }
          />
          <OrderCustomer customer={hydratedCustomer} />
          <OrderSupport store={order?.store} />
          <UrbanPiperFooter />
          {pendingPay && (
            <BottomFooter>
              <div className="flex items-center gap-x-6">
                {awaitingCheckout &&
                  !isDeliveryWoAddress &&
                  (applicablePaymentMethods ?? [])?.length > 1 && (
                    <div
                      className="shrink-0 cursor-pointer"
                      onClick={() => {
                        setPaymentModalOpen(true);
                      }}>
                      <div className="flex items-center gap-x-2 text-up-black-50">
                        {order?.details?.pay_by === ApiConstants.PAY_BY_CASH ? (
                          <PayTimeIcon className="w-4 h-4" />
                        ) : (
                          <PhoneIcon className="w-4 h-4" />
                        )}
                        <span className="text-sm">Pay</span>
                        <ChevronUpIcon />
                      </div>
                      <p className="text-sm font-bold mt-1">
                        {order?.details?.pay_by === ApiConstants.PAY_BY_CASH
                          ? `On ${order?.details?.order_type}`
                          : 'Right now'}
                      </p>
                    </div>
                  )}
                {isDeliveryWoAddress ? (
                  <UpButton
                    onClick={() => {
                      if (customerAddresses?.length) {
                        updateTypeModalOpen(true);
                      } else {
                        void navigate(`/${orderId}/new-address`);
                      }
                    }}>
                    Continue
                  </UpButton>
                ) : (
                  <UpButton onClick={onPayClick} loading={isPending}>
                    {order?.details?.pay_by === ApiConstants.PAY_BY_CASH
                      ? 'Confirm Order'
                      : `Proceed to Pay ${formatMoney(order?.details?.payable_amount ?? '')}`}
                  </UpButton>
                )}
              </div>
            </BottomFooter>
          )}
          <PaymentTypeModal
            order={order}
            orderId={orderId}
            customer={hydratedCustomer}
            isOpen={paymentModalOpen}
            onClose={() => {
              setPaymentModalOpen(false);
            }}
          />
          {tippingEnabled && (
            <TipModal
              order={order}
              orderId={orderId}
              customer={hydratedCustomer}
              presets={tippingPresets}
              isOpen={tipModalOpen}
              onClose={() => {
                setTipModalOpen(false);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function OrderViewSkeleton() {
  return (
    <div className="px-2 py-4 overflow-x-hidden space-y-4">
      <Skeleton header />
      <Skeleton rows={2} />
      <Skeleton rows={4} />
      <Skeleton rows={3} />
    </div>
  );
}

export default OrderView;
